import {
  getAllInvitedUsersInOrganization,
  getAllUsersInOrganization,
  getUser
} from '@/services/userService'
import React, { useEffect, ReactNode } from 'react'
import { Sidebar } from '@/components/custom/Sidebar'
import { useCookies } from 'react-cookie'
import { useNavigate, useLocation } from "react-router-dom"
import { useSelector, useDispatch } from 'react-redux'
import { userActions } from '@/redux/slice/userSlice'
import { AppDispatch, RootState } from '@/redux'
import { appActions } from '@/redux/slice/appSlice'
import { Auth } from '@/API/Auth'
import { Toaster } from '@/components/ui/toaster'
import { adminRoutes } from '@/constants/admin/adminRoutes'
import { Loading } from '@/components/custom/Loading/Loading'
import { SkipLink } from '@/components/custom/SkipLink'
import { getAllOrganizations } from '@/services/organizationService'
import { getAllWorkflows } from '@/services/workflowService'
import { getAllApplications } from '@/services/applicationsService'
import { organizationActions } from '@/redux/slice/organizationSlice'
import { workflowActions } from '@/redux/slice/workflowSlice'
import { credentialActions } from '@/redux/slice/credentialSlice'
import { getCredentials } from '@/services/credentialsService'
import { getInvoices, getOneTimeFees, getSubscription } from '@/services/billingService'
import { getAllTransformationTemplates } from '@/services/transformationService'
import { getAdminMrr, getAdminTransactions } from '@/services/adminDashboardService'
import { getDashboards } from '@/services/dashboardsService'

interface ProtectedRouteLayoutProps {
  component: ReactNode
}

export const ProtectedRouteLayout: React.FC<ProtectedRouteLayoutProps> = ({ component }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { loading } = useSelector((state: RootState) => state.app)
  const { userData } = useSelector((state: RootState) => state.user)
  const dispatch: AppDispatch = useDispatch()
  const [cookies, setCookie, removeCookie] = useCookies(['tcsAccessToken', 'tcsRefreshToken'])

  useEffect(() => {
    if (adminRoutes.includes(location.pathname.split('/')[1])) {
      if (userData?.isTCSAdmin === false) {
        navigate('/workflows')
      }
    }
  }, [location.pathname, navigate, userData])

  useEffect(() => {
    const logout = () => {
      removeCookie('tcsRefreshToken', { domain: document.location.hostname, path: '/' })
      removeCookie('tcsAccessToken', { domain: document.location.hostname, path: '/' })
      dispatch(userActions.logoutUser())
      dispatch(credentialActions.reset())
      dispatch(organizationActions.reset())
      dispatch(workflowActions.reset())
      navigate('/login')
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fetchWithAuthCheck = async (apiCall: Promise<any>) => {
      try {
        const response = await apiCall
        if (response?.err === 'missing or invalid auth token') {
          logout()
          throw new Error('Unauthorized')
        }
        return response
      } catch (error) {
        console.error("API Call Error:", error);
        logout()
        throw error
      }
    }
    const loadAllData = async (token: string) => {
      const userResp = await dispatch(getUser(token))
      if (userResp?.user?.isTCSAdmin) {
        // fetch admin data in parallel since there's no org-specific logic needed to access it
        // Dashboard admin data
        await fetchWithAuthCheck(dispatch(getAdminMrr(token)))
        await fetchWithAuthCheck(dispatch(getAdminTransactions(token)))
      }
      const allOrganizationsResp = await dispatch(getAllOrganizations(token))
      if (
        userResp?.user?.currentOrganization === null || // TODO This should be AND condition after update in the backend
        allOrganizationsResp?.data?.organizations?.length === 0
      ) {
        return navigate('/no-organization')
      } else {
        fetchWithAuthCheck(dispatch(getAllUsersInOrganization(token)))
        fetchWithAuthCheck(dispatch(getCredentials(token)))
        fetchWithAuthCheck(dispatch(getAllInvitedUsersInOrganization(token)))
        fetchWithAuthCheck(dispatch(getAllApplications(token)))
        fetchWithAuthCheck(dispatch(getAllWorkflows(token)))
        fetchWithAuthCheck(dispatch(getAllTransformationTemplates(token)))
        fetchWithAuthCheck(dispatch(getDashboards(token)))
      }
      // not for 'user' role
      if (userResp?.user?.role !== 'user') {
        const orgId = userResp?.user?.currentOrganization?.id as string
        fetchWithAuthCheck(dispatch(getSubscription(token, orgId)))
        fetchWithAuthCheck(dispatch(getInvoices(token, orgId)))
        fetchWithAuthCheck(dispatch(getOneTimeFees(token, orgId)))
      }
    }

    const loadUserAndUpdateToken = async () => {
      if (!cookies.tcsAccessToken && cookies.tcsRefreshToken) {
        dispatch(appActions.setLoading(true))
        Auth.getAccessTokenFromRefreshToken({
          refreshToken: cookies.tcsRefreshToken as string
        })
          .then(async res => {
            if (res.data) {
              setCookie('tcsRefreshToken', res.data.refreshToken.token, {
                domain: document.location.hostname,
                path: '/',
                expires: new Date(res.data.refreshToken.expiresAt)
              })
              setCookie('tcsAccessToken', res.data.accessToken.token, {
                domain: document.location.hostname,
                path: '/',
                expires: new Date(res.data.accessToken.expiresAt)
              })
              dispatch(userActions.setUserToken(res.data.accessToken.token))
              await loadAllData(res.data.accessToken.token)
            } else {
              logout()
            }
          })
          .catch((err: unknown) => {
            console.log(err)
            logout()
          })
          .finally(() => {
            // Always clear the loading flag set at the start of the refresh flow.
            // Without this, app.loading stays true permanently after a token refresh,
            // rendering a position:fixed overlay that blocks all user interactions.
            dispatch(appActions.setLoading(false))
          })
      } else if (cookies.tcsAccessToken) {
        dispatch(userActions.setUserToken(cookies.tcsAccessToken as string))
        if (!userData) {
          await loadAllData(cookies.tcsAccessToken as string)
        }
      } else {
        logout()
      }
    }

    loadUserAndUpdateToken()
      .catch((err: unknown) => {
        console.log(err)
        logout()
      })
  }, [cookies, dispatch, userData, navigate, setCookie, removeCookie])

  return (
    <div className='w-full h-full flex flex-row'>
      <SkipLink />
      {loading && <Loading />}
      <Sidebar />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        {component}
      </main>
      <Toaster />
    </div>
  )
}
